import React, { useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Palette, radius, spacing, useThemePalette } from '../theme';

interface TagsProps {
  tags?: string[];
}

// Job feeds attach long, noisy tag lists (40+ entries is common) that used to
// dominate the description modal. Collapse to a preview row with a "+N" pill;
// tapping expands the full list, and a chevron collapses it again.
const COLLAPSED_COUNT = 6;

const Tags: React.FC<TagsProps> = ({ tags }) => {
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [expanded, setExpanded] = useState(false);

  if (!tags || tags.length === 0) {
    return null;
  }

  const collapsible = tags.length > COLLAPSED_COUNT;
  const visible = expanded || !collapsible ? tags : tags.slice(0, COLLAPSED_COUNT);
  const hiddenCount = tags.length - COLLAPSED_COUNT;

  return (
    <View style={styles.container}>
      <View style={styles.tagsView}>
        {visible.map((tag, i) => (
          <Text style={styles.tag} key={i}>
            {tag}
          </Text>
        ))}
        {collapsible ? (
          <TouchableOpacity
            onPress={() => setExpanded((e) => !e)}
            activeOpacity={0.7}
            accessibilityRole="button"
          >
            {expanded ? (
              <View style={styles.toggle}>
                <MaterialCommunityIcons
                  name="chevron-up"
                  size={14}
                  color={palette.primary}
                />
              </View>
            ) : (
              <Text style={[styles.tag, styles.toggleTag]}>+{hiddenCount}</Text>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: {
      marginVertical: spacing.sm,
    },
    tagsView: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 6,
    },
    tag: {
      fontSize: 11,
      textTransform: 'uppercase',
      backgroundColor: p.surfaceMuted,
      color: p.textSecondary,
      borderRadius: radius.sm,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
      overflow: 'hidden',
      fontWeight: '600',
    },
    toggleTag: {
      color: p.primary,
    },
    toggle: {
      backgroundColor: p.surfaceMuted,
      borderRadius: radius.sm,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
      flexDirection: 'row',
      alignItems: 'center',
    },
  });

export default Tags;
